<template>
  <v-dialog
    :value="visiblePopup"
    :max-width="1100"
    @click:outside="onOkClickButton"
    @keydown.esc="onOkClickButton"
  >
    <div class="compliance-statistic-popup">
      <div class='close_btn' @click='onOkClickButton'>
        <span class="material-icons-outlined">
          close
        </span>
      </div>
      <div class='compliance-statistic-popup__header'>
        <div class="title">{{$t(title)}}</div>
        <v-divider class='compliance-statistic-popup__header__divider'  vertical />
        <div class="message">
          <div class='compliance-statistic-sections'>
            <div class="sub-title">{{ $t('asset.count.details.popup.cloud.account.title') }}</div>
            <div class='sub-title__content__domains' :class='{closed: !isShowAll}' > {{ cloudAccounts.join(', ') }} </div>
          </div>
          <div v-if='isCloudAccountsListLong' class='hide-domains-btn' @click.stop="isShowAll = !isShowAll">
            <span>{{ captionOfShowAllToggleButton }}</span>
          </div>
          <div v-else class='hide-domains-btn'/>
      </div>
      </div>
      <div class='compliance-statistic-popup__content'>
        <stephen-von-worley-data-chart
          :stats='stats'
          :formatDataForCirclesChart='formatDataForCirclesChart'
          :formatDataForBarChart='formatDataForBarChart'
          :visiblePopup='visiblePopup'
        />
      </div>
      <div class="buttons">
        <v-btn small depressed class="yes-button btn-primary" @click="onOkClickButton()">
          {{$t(okCaption)}}
        </v-btn>
      </div>
    </div>
  </v-dialog>

</template>

<script>

import { mapGetters } from 'vuex';
import StephenVonWorleyDataChart from '@/components/charts/StephenVonWorleyDataChart.vue';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';

export default {
  name: 'ComplianceStatisticPopup',
  mixins: [disableOuterScrollOnPopupVisible],
  components: { StephenVonWorleyDataChart },
  props: {
    stats: {
      type: Object,
      required: true,
    },
    visiblePopup: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      isShowAll: false,
      displayChart: false,
    };
  },
  computed: {
    ...mapGetters(['cloudAccountsPerOrganization', 'organizationFilter', 'domainFilter']),
    hasData() {
      return Object.keys(this.stats).length > 0;
    },
    formatDataForCirclesChart() {
      const formattedData = [];
      if (this.hasData) {
        const tests = this.stats.byCheckType;
        Object.keys(tests).forEach(key => {
          formattedData.push({
            label: this.formattedLabel(key),
            data: this.formatStatsData(tests[key]),
          });
        });
      }
      return formattedData;
    },
    formatDataForBarChart() {
      const formattedData = [];
      if (this.hasData) {
        const totals = this.stats.total;
        Object.keys(totals).forEach(key => {
          formattedData.push({
            label: this.formattedLabel(key),
            data: {
              fail: totals[key].FAIL,
              pass: totals[key].PASS,
              info: totals[key].INFO,
            },
          });
        });
      }
      return formattedData;
    },
    isCloudAccountsListLong() {
      return this.cloudAccounts.join(', ').length > 100;
    },
    title() {
      return 'compliance.statistic.popup.title';
    },
    okCaption() {
      return 'confirm.ok';
    },
    captionOfShowAllToggleButton() {
      return this.isShowAll ? this.$t('detailed.issue.show.less.list') : this.$t('detailed.issue.show.more.list');
    },
    cloudAccounts() {
      const result = [];
      this.organizationFilter.forEach(org => {
        const cloudAccountsFromSelectedOrganizations = this.cloudAccountsPerOrganization.get(org);
        if (cloudAccountsFromSelectedOrganizations) {
          cloudAccountsFromSelectedOrganizations.forEach(cloudAccount => {
            if (this.domainFilter.includes(cloudAccount)) {
              result.push(cloudAccount);
            }
          });
        }
      });
      return result;
    },
  },
  methods: {
    formatStatsData(categories) {
      const formattedData = [];
      Object.keys(categories).forEach(categorie => {
        formattedData.push({
          tooltip: categorie,
          fail: categories[categorie].FAIL,
          pass: categories[categorie].PASS,
          info: categories[categorie].INFO,
        });
      });
      return formattedData;
    },
    formattedLabel(label) {
      const regex = /(?<=[A-Za-z])-(?=[A-Za-z\d])|(?<=[A-Za-z\d])-(?=[A-Za-z])/g;
      let formattedData = label.replace(regex, ' ');
      formattedData = formattedData.replace('Revision ', 'rev:');
      return formattedData;
    },
    onOkClickButton() {
      this.$emit('clicked', true);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/styles/design-system';

.compliance-statistic-popup {
  max-width: rem(1100);
  padding: $space-8 $space-7;
  display: flex;
  flex-direction: column;
  background-color: var(--bg-primary);

  &__header{
    display: flex;
    flex-direction: row;
    margin-bottom: $space-2;
    &__divider {
      margin-left: $space-4;
      margin-right: $space-4;
    }
  }
  .close_btn {
    display: flex;
    align-self: flex-end;
    margin-top: rem(-26);
    margin-right: rem(-20);
    color: var(--text-secondary);
    &:hover {
      cursor: pointer;
      color: var(--brand-primary);
    }
  }
  &__content {
    min-height: rem(300);
  }
  .title {
    margin-top: rem(-2);
    color: var(--text-primary);
    font-family: $default-application-font-bold;
    text-transform: capitalize;
    font-size: $font-size-2xl;
    font-weight: bold;
    line-height: $font-size-xl;
    letter-spacing: 0;
  }
  .sub-title{
    color: var(--text-secondary);
    font-family: $default-application-font-regular;
    letter-spacing: 1.1px;
    line-height: $font-size-md;
    text-transform: uppercase;
    font-size: $font-size-sm;
    padding-bottom: $space-1;
    &__content {
      &__domains {
        text-transform: uppercase;
        font-size: $font-size-md;
        font-family: $default-application-font-medium;
        line-height: $font-size-2xl;
        &.closed {
          padding-top: $space-1;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
          -webkit-line-clamp: 1;
        }
      }
    }
  }

  .message {
    font-size: $font-size-base;
    line-height: 1.36;
    font-weight: normal;
    color: var(--text-primary);
    letter-spacing: 0;
    display: flex;
    flex-direction: column;

    .compliance-statistic-sections{
      display: flex;
      flex-direction: column;
      min-height: rem(80);
    }
    .hide-domains-btn {
      color: var(--brand-primary);
      font-family: $default-application-font-regular;
      font-size: $font-size-base;
      letter-spacing: 0;
      margin-top: rem(-20);
      margin-bottom: $space-8;
      text-align: right;
      width: 100%;
      &:hover {
        cursor: pointer;
      }
    }
    .assets-count-details-sections {
      font-family: $default-application-font-semi-bold;
      font-size: $font-size-2xl;
    }
  }
  .buttons {
    text-align: right;
    padding-top: $space-4;
    .yes-button {
      height: $btn-height-md;
      width: rem(51);
      border-radius: $radius-sm;
      background-color: var(--brand-primary);
      box-shadow: $shadow-sm;
      color: var(--text-inverse);
      font-size: $font-size-sm;
      font-weight: 500;
      letter-spacing: 1.07px;
      line-height: $font-size-md;
      text-align: center;
      margin-left: $space-2;
      margin-right: rem(-10);
      margin-bottom: rem(-14);
    }
  }
}

</style>
